import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  useTheme,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
  CircularProgress,
  Divider,
  useMediaQuery,
  Fade,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

// Icons
import SendIcon from "@mui/icons-material/Send";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PlaceIcon from "@mui/icons-material/Place";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailIcon from "@mui/icons-material/Email";
import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import BusinessIcon from "@mui/icons-material/Business";
import SubjectIcon from "@mui/icons-material/Subject";
import CategoryIcon from "@mui/icons-material/Category";
import MessageIcon from "@mui/icons-material/Message";

const ContactPage = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    interest: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.title =
      "SGT Global Trade | " + (i18n.language === "tr" ? "İletişim" : "Contact");
  }, [i18n.language]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name =
        i18n.language === "tr" ? "İsim gerekli" : "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email =
        i18n.language === "tr" ? "E-posta gerekli" : "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email =
        i18n.language === "tr"
          ? "Geçerli bir e-posta adresi girin"
          : "Enter a valid email address";
    }

    if (!formData.message.trim()) {
      errors.message =
        i18n.language === "tr" ? "Mesaj gerekli" : "Message is required";
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Set loading state
    setLoading(true);

    // Simulate form submission and redirect preparation
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setSnackbarOpen(true);

      // Create mailto link
      const subject = encodeURIComponent(
        formData.subject ||
          (i18n.language === "tr" ? "İletişim Formu" : "Contact Form")
      );
      const body = encodeURIComponent(`
        İsim/Name: ${formData.name}
        E-posta/Email: ${formData.email}
        Telefon/Phone: ${formData.phone || ""}
        Şirket/Company: ${formData.company || ""}
        İlgilenilen Ürün/Product of Interest: ${formData.interest || ""}
        
        Mesaj/Message:
        ${formData.message}
      `);

      // Redirect after showing message
      setTimeout(() => {
        window.location.href = `mailto:info@sgttr.com?subject=${subject}&body=${body}`;

        // Reset form after redirect
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
          interest: "",
        });

        // Reset submission status after delay
        setTimeout(() => {
          setSubmitted(false);
        }, 1000);
      }, 3000);
    }, 1500);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Animation variants
  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerItems = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemFade = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  // Custom form input styling
  const inputProps = {
    sx: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover, &.Mui-focused": {
          boxShadow: "0 4px 10px rgba(0,0,0,0.07)",
          transform: "translateY(-2px)",
        },
      },
      "& .MuiInputLabel-root": {
        fontWeight: 500,
      },
      "& .MuiInputBase-input": {
        fontWeight: 500,
      },
    },
  };

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Header Section */}
      <Box
        sx={{
          backgroundColor: "#f5f7fa",
          py: { xs: 5, md: 7 }, // Azaltılmış padding
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Elements */}
        <Box
          sx={{
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            opacity: 0.07,
            top: "-200px",
            right: "-100px",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
            opacity: 0.07,
            bottom: "-150px",
            left: "-100px",
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div initial="hidden" animate="visible" variants={fadeUp}>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, // Reduced size
                    fontWeight: 800,
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "-0.5px",
                  }}
                >
                  {i18n.language === "tr" ? "Bize Ulaşın" : "Get In Touch"}
                </Typography>

                <Typography
                  variant="h2"
                  component="p"
                  sx={{
                    fontSize: { xs: "1.1rem", md: "1.3rem" }, // Reduced size
                    fontWeight: 400,
                    color: "text.secondary",
                    mb: 3,
                    maxWidth: "90%",
                    lineHeight: 1.6,
                  }}
                >
                  {i18n.language === "tr"
                    ? "Sorularınız ve ihtiyaçlarınız için bizimle iletişime geçin. Size yardımcı olmak için buradayız."
                    : "Reach out to us for any questions or needs. We're here to help you."}
                </Typography>

                <motion.div
                  variants={staggerItems}
                  initial="hidden"
                  animate="visible"
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <motion.div variants={itemFade}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                            height: "100%",
                            transition: "transform 0.3s, box-shadow 0.3s",
                            "&:hover": {
                              transform: "translateY(-5px)",
                              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                            },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              mb: 2,
                              width: 48,
                              height: 48,
                            }}
                          >
                            <LocalPhoneIcon sx={{ color: "primary.main" }} />
                          </Avatar>
                          <Typography
                            variant="h6"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            {i18n.language === "tr" ? "Telefon" : "Phone"}
                          </Typography>
                          <Typography
                            variant="body1"
                            component="a"
                            href="tel:+905333992188"
                            sx={{
                              color: "text.primary",
                              textDecoration: "none",
                              "&:hover": {
                                color: "primary.main",
                              },
                              fontWeight: 500,
                            }}
                          >
                            +90 533 399 2188
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <motion.div variants={itemFade}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                            height: "100%",
                            transition: "transform 0.3s, box-shadow 0.3s",
                            "&:hover": {
                              transform: "translateY(-5px)",
                              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                            },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              mb: 2,
                              width: 48,
                              height: 48,
                            }}
                          >
                            <MailOutlineIcon sx={{ color: "secondary.main" }} />
                          </Avatar>
                          <Typography
                            variant="h6"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            {i18n.language === "tr" ? "E-posta" : "Email"}
                          </Typography>
                          <Typography
                            variant="body1"
                            component="a"
                            href="mailto:info@sgttr.com"
                            sx={{
                              color: "text.primary",
                              textDecoration: "none",
                              "&:hover": {
                                color: "secondary.main",
                              },
                              fontWeight: 500,
                            }}
                          >
                            info@sgttr.com
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                    <Grid item xs={12}>
                      <motion.div variants={itemFade}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                            transition: "transform 0.3s, box-shadow 0.3s",
                            "&:hover": {
                              transform: "translateY(-5px)",
                              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                            },
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 2,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              width: 48,
                              height: 48,
                            }}
                          >
                            <PlaceIcon sx={{ color: "success.main" }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              {i18n.language === "tr" ? "Adres" : "Address"}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: "text.secondary",
                                lineHeight: 1.6,
                                fontWeight: 500,
                              }}
                            >
                              İNKILAP MH. SEYİT ONBAŞI CD. NO: 7/A
                              <br />
                              ÜMRANİYE / İSTANBUL, TÜRKİYE
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            sx={{
                              ml: { xs: 0, sm: "auto" },
                              mt: { xs: 2, sm: 0 },
                              borderRadius: 2,
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                            href="https://maps.app.goo.gl/YYaJhBENt1ewjKWM8"
                            target="_blank"
                          >
                            {i18n.language === "tr"
                              ? "Haritada Görüntüle"
                              : "View on Map"}
                          </Button>
                        </Paper>
                      </motion.div>
                    </Grid>
                  </Grid>
                </motion.div>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* ===== MODERNIZED CONTACT FORM ===== */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                    position: "relative",
                    backdropFilter: "blur(10px)",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    transition: "transform 0.3s, box-shadow 0.3s",
                    "&:hover": {
                      boxShadow: "0 15px 50px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: "6px",
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }}
                  />
                  <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: "text.primary",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <SendIcon
                        fontSize="small"
                        sx={{
                          color: theme.palette.primary.main,
                          transform: "rotate(-25deg)",
                        }}
                      />
                      {i18n.language === "tr"
                        ? "Mesaj Gönderin"
                        : "Send a Message"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2.5,
                        color: "text.secondary",
                      }}
                    >
                      {i18n.language === "tr"
                        ? "Formu doldurduğunuzda e-posta uygulamanıza yönlendirileceksiniz."
                        : "When you submit the form, you will be redirected to your email application."}
                    </Typography>
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={
                              i18n.language === "tr"
                                ? "İsim Soyisim"
                                : "Full Name"
                            }
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            variant="outlined"
                            required
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            disabled={submitted || loading}
                            size="medium"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonOutlineIcon color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            {...inputProps}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={i18n.language === "tr" ? "E-posta" : "Email"}
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            variant="outlined"
                            required
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            disabled={submitted || loading}
                            size="medium"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            {...inputProps}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={i18n.language === "tr" ? "Telefon" : "Phone"}
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            variant="outlined"
                            disabled={submitted || loading}
                            size="medium"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneEnabledIcon color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            {...inputProps}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={
                              i18n.language === "tr" ? "Şirket" : "Company"
                            }
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            variant="outlined"
                            disabled={submitted || loading}
                            size="medium"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BusinessIcon color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            {...inputProps}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={i18n.language === "tr" ? "Konu" : "Subject"}
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            variant="outlined"
                            disabled={submitted || loading}
                            size="medium"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SubjectIcon color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            {...inputProps}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="medium"
                            sx={{
                              ...inputProps.sx,
                              "& .MuiInputLabel-outlined": {
                                maxWidth: "none", // Remove any max-width restrictions
                                transform: "translate(14px, 16px) scale(1)",
                                whiteSpace: "nowrap", // Prevent label text from wrapping
                                overflow: "visible", // Ensure text is not cut off
                                "&.MuiInputLabel-shrink": {
                                  transform:
                                    "translate(14px, -6px) scale(0.75)",
                                },
                              },
                              "& .MuiSelect-select": {
                                fontSize: "1rem",
                                fontWeight: 500,
                                paddingTop: "15px",
                                paddingBottom: "15px",
                                paddingLeft: "48px", // Increased to accommodate the icon
                              },
                              "& .MuiOutlinedInput-root": {
                                paddingRight: "14px", // Ensure enough space for dropdown icon
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "1rem", // Ensure consistent font size
                              },
                            }}
                          >
                            <InputLabel>
                              {i18n.language === "tr"
                                ? "İlgilendiğiniz Ürün"
                                : "Product of Interest"}
                            </InputLabel>
                            <Select
                              name="interest"
                              value={formData.interest}
                              onChange={handleInputChange}
                              label={
                                i18n.language === "tr"
                                  ? "İlgilendiğiniz Ürün"
                                  : "Product of Interest"
                              }
                              disabled={submitted || loading}
                              displayEmpty
                              placeholder={
                                i18n.language === "tr" ? "Seçiniz" : "Select"
                              }
                              startAdornment={
                                <InputAdornment
                                  position="start"
                                  sx={{ ml: 1, minWidth: "24px" }}
                                >
                                  <CategoryIcon color="primary" />
                                </InputAdornment>
                              }
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                    width: "auto",
                                  },
                                },
                              }}
                            >
                              <MenuItem value="wheat">
                                {i18n.language === "tr" ? "Buğday" : "Wheat"}
                              </MenuItem>
                              <MenuItem value="barley">
                                {i18n.language === "tr" ? "Arpa" : "Barley"}
                              </MenuItem>
                              <MenuItem value="corn">
                                {i18n.language === "tr" ? "Mısır" : "Corn"}
                              </MenuItem>
                              <MenuItem value="corn-oil">
                                {i18n.language === "tr"
                                  ? "Mısır Yağı"
                                  : "Corn Oil"}
                              </MenuItem>
                              <MenuItem value="sunflower-oil">
                                {i18n.language === "tr"
                                  ? "Ayçiçek Yağı"
                                  : "Sunflower Oil"}
                              </MenuItem>
                              <MenuItem value="chicken">
                                {i18n.language === "tr" ? "Tavuk" : "Chicken"}
                              </MenuItem>
                              <MenuItem value="fertilizer">
                                {i18n.language === "tr"
                                  ? "Gübre"
                                  : "Fertilizer"}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={
                              i18n.language === "tr"
                                ? "Mesajınız"
                                : "Your Message"
                            }
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            variant="outlined"
                            multiline
                            rows={4}
                            required
                            error={!!formErrors.message}
                            helperText={formErrors.message}
                            disabled={submitted || loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  sx={{ mt: 1.5, alignSelf: "flex-start" }}
                                >
                                  <MessageIcon color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            {...inputProps}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 1,
                            }}
                          >
                            <Button
                              type="submit"
                              variant="contained"
                              disabled={submitted || loading}
                              startIcon={loading ? null : <SendIcon />}
                              sx={{
                                py: 1.2,
                                px: { xs: 2, md: 3 },
                                height: "48px",
                                borderRadius: "24px", // Pill-shaped button
                                fontWeight: 600,
                                textTransform: "none",
                                fontSize: "1rem",
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                transition: "all 0.3s",
                                "&:hover": {
                                  boxShadow: `0 8px 20px ${theme.palette.primary.main}40`,
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              {loading ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : submitted ? (
                                i18n.language === "tr" ? (
                                  "E-posta Uygulamasına Yönlendiriliyor..."
                                ) : (
                                  "Redirecting to Email Application..."
                                )
                              ) : i18n.language === "tr" ? (
                                "Mesaj Gönder"
                              ) : (
                                "Send Message"
                              )}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Map Section */}
      <Box sx={{ py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h4"
              component="h2"
              align="center"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: "1.75rem", md: "2.25rem" },
              }}
            >
              {i18n.language === "tr" ? "Bizi Ziyaret Edin" : "Visit Us"}
            </Typography>

            <Typography
              variant="body1"
              align="center"
              sx={{
                color: "text.secondary",
                mb: 4,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              {i18n.language === "tr"
                ? "Ofisimiz Ümraniye'de kolay ulaşılabilir bir konumda bulunmaktadır."
                : "Our office is located in an easily accessible location in Ümraniye."}
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Paper
              elevation={0}
              sx={{
                height: "500px",
                width: "100%",
                overflow: "hidden",
                borderRadius: 3,
                boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
              }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d6018.961204800793!2d29.108473999999998!3d41.036618!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac91c6cd472f7%3A0x8ee4cc22982beb40!2zxLBua8SxbGFwLCBTZXnEsHQgT25iLiBTb2thZ8SxIE5vOjcgRDphLCAzNDc2OCDDnG1yYW5peWUvxLBzdGFuYnVs!5e0!3m2!1str!2str!4v1744229369343!5m2!1str!2str"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SGT Global Trade Office Location"
              ></iframe>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Scroll to top button */}
      <Fade in={showScrollTop}>
        <Box
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 10,
            cursor: "pointer",
          }}
        >
          <IconButton
            color="primary"
            sx={{
              bgcolor: "white",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              "&:hover": {
                bgcolor: "white",
                transform: "translateY(-3px)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
              },
              transition: "all 0.3s",
            }}
          >
            <ArrowUpwardIcon />
          </IconButton>
        </Box>
      </Fade>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {i18n.language === "tr"
            ? "E-posta uygulamanıza yönlendiriliyorsunuz, lütfen bekleyin..."
            : "You are being redirected to your email application, please wait..."}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Helper function for color manipulation
const alpha = (color, opacity) => {
  return opacity === undefined
    ? color
    : `${color}${Math.round(opacity * 255)
        .toString(16)
        .padStart(2, "0")}`;
};

export default ContactPage;
